import * as _ from "lodash";
import * as moment from "moment";
import { ClientRepo as clientsRepo } from "@services/clients/client.repo";
import { ReportsRepo as reportsRepo } from "@services/reports.repo";
import { ReportingService as reportingService } from "@common/reporting.service";
import { Boolean } from "aws-sdk/clients/marketplaceentitlementservice";
import * as echarts from "echarts";

class ReportsController {
  private clients: Array<any>;
  private reportTypes: Array<String>;
  private reports: any = {};
  public documentGenerating: Boolean;
  public displayMulitpleClientReports: Boolean = false;
  public isLoadingReports: Boolean = false;

  public inceptionPerformanceChart: any;
  public inceptionPerformanceChartOptions: any;

  /** @ngInject */
  constructor(
    private clientsRepo: clientsRepo,
    private reportsRepo: reportsRepo,
    private reportingService: reportingService,
    private notifyjs
  ) {
    this.documentGenerating = false;
    this.displayMulitpleClientReports = false;
    this.getUserClients().then(() => {
      this.getReportTypes().then(() => {
        _.forEach(this.reportTypes, (reportType) => {
          // at first just fetch the reports for the first client
          this.getReportsForReportType(
            this.clients[0].clientNumber,
            reportType
          );
        });
      });
    });
  }

  private getUserClients() {
    this.isLoadingReports = true;
    return this.clientsRepo
      .getUserClients()
      .then((res) => {
        if (res && res.data) {
          this.clients = res.data as Array<any>;
        }
        this.displayMulitpleClientReports = this.clients.length > 1;
        this.isLoadingReports = false;
      })
      .catch(() => {
        this.isLoadingReports = false;
        this.notifyjs(
          "The reports could not be loaded. Please try again later or contact the System Administrator.",
          "error"
        );
      });
  }

  private getReportTypes() {
    this.isLoadingReports = true;
    return this.reportsRepo
      .getReportTypes()
      .then((res) => {
        this.isLoadingReports = false;
        if (res && res.data)
          this.reportTypes = _.map(res.data as Array<any>, (reportType) => {
            return reportType.name;
          });
      })
      .catch(() => {
        this.isLoadingReports = false;
        this.notifyjs(
          "The reports could not be loaded. Please try again later or contact the System Administrator.",
          "error"
        );
      });
  }

  private getReportsForReportType(clientNumber, reportType) {
    this.isLoadingReports = true;
    return this.reportsRepo
      .getClientReportByType(clientNumber, reportType)
      .then((reportDetails) => {
        this.reports[reportType] = [];

        _.forEach(reportDetails, (report) => {
          const displayDate = moment(report.reportDate).format("MMMM Y");
          let currentReport = _.find(
            this.reports[reportType],
            (r) => r["date"] == displayDate
          );
          if (!currentReport) {
            currentReport = {
              date: displayDate,
              type: reportType,
              isLocked: report.isLocked,
              reports: [],
              custodianReports: report.custodianReports,
            };
            this.reports[reportType].push(currentReport);
          }

          // console.log("currentReport ", currentReport);

          const reportDetails = {
            reportId: report.reportId,
            bucket: report.bucketName,
            reportName: "",
            key: report.reportKey,
          };

          if (report.externalSource === null) {
            reportDetails.reportName = `Confident (${report.reportCurrency})`;
          } else if (report.externalSource === "MCB") {
            reportDetails.reportName = "MCB";
          } else if (report.externalSource === "Rothschild") {
            reportDetails.reportName = "EDR";
          }

          currentReport.reports.push(reportDetails);
        });
      })
      .catch(() =>
        this.notifyjs(
          "The reports could not be loaded. Please try again later or contact the System Administrator.",
          "error"
        )
      )
      .finally(() => (this.isLoadingReports = false));
  }

  public formatToStartCase(word) {
    return _.startCase(word);
  }

  public getDownloadCountOutput(report: any) {
    let keys = Object.keys(report.reports);
    let count = keys.length;
    if (count == 1) return "1 report";
    else return `${count} reports`;
  }

  public openReport(reportDetails: any) {
    this.reportingService.openReport(reportDetails.key);
  }

  public formatCustodianReportName(custodianReport: any): string {
    if (custodianReport.name) {
      // Use name as is
      return custodianReport.name;
    } else {
      // Fallback to the old format
      return `${custodianReport.externalSource} ${custodianReport.accountNumber} (${custodianReport.reportCurrency}) Report`;
    }
  }

  public formatReportName(report: any): string {
    // Use reportName as is (backend already handles formatting)
    return report.reportName || '';
  }

  public generateReportsForClient(clientNumber) {
    _.forEach(this.reportTypes, (reportType) => {
      // at first just fetch the reports for the first client
      this.getReportsForReportType(clientNumber, reportType);
    });
  }

  public getCombinedReportsForReportTypes() {
    _.forEach(this.reportTypes, (reportType) => {
      this.getCombinedReportForUser(reportType);
    });
  }

  public getCombinedReportForUser(reportType) {
    this.isLoadingReports = true;
    return this.reportsRepo
      .getCombinedReportsForUser(reportType)
      .then((res) => {
        let reports = res.data;
        this.reports[reportType] = [];

        _.forEach(res.data as Array<any>, (report) => {
          let date = moment(report.dateGenerated);
          let displayDate = date.format("MMMM Y");

          let index = _.findIndex(this.reports[reportType], (r) => {
            return r["date"] == displayDate;
          });
          let currentReport = {
            date: displayDate,
            type: reportType,
            reports: {},
          };
          if (index != -1) currentReport = this.reports[reportType][index];
          else this.reports[reportType].push(currentReport);

          let reportDetails = {
            bucket: report.bucketName,
            key: report.reportKey,
          };

          if (report.externalSource == null)
            currentReport.reports["Confident"] = reportDetails;

          if (
            report.externalSource != null &&
            report.externalSource.name == "MCB"
          )
            currentReport.reports["MCB"] = reportDetails;

          if (
            report.externalSource != null &&
            report.externalSource.name == "Rothschild"
          )
            currentReport.reports["EDR"] = reportDetails;
        });
        this.isLoadingReports = false;
      })
      .catch(() => {
        this.isLoadingReports = false;
        this.notifyjs(
          "The reports could not be loaded. Please try again later or contact the System Administrator.",
          "error"
        );
      });
  }

  getInceptionPerf = () => {
    this.reportsRepo
      .getInceptionPerformance([489, 490], null)
      .then((res) => {
        var inceptionPerfItems = res.data as any;

        this.inceptionPerformanceChartOptions = {
          title: {
            text: `Performance`,
          },
          grid: {
            left: "3%",
            right: "4%",
            bottom: "3%",
            containLabel: true,
          },
          xAxis: {
            type: "value",
            name: ``,
            nameLocation: "start",
            nameTextStyle: {
              fontWeight: "bold",
            },
            splitInterval: 1,
          },
          yAxis: {
            type: "value",
            data: inceptionPerfItems.PerformanceGraph.map((perf) => perf.year),
          },
          series: [
            {
              type: "bar",
              data: inceptionPerfItems.PerformanceGraph.map(
                (perf) => perf.Performance
              ),
            },
            {
              type: "line",
              data: inceptionPerfItems.PerformanceGraph.map(
                (perf) => perf.CumulativePerformance
              ),
            },
          ],
        };
        this.inceptionPerformanceChart.setOption(
          this.inceptionPerformanceChartOptions
        );
      })
      .catch((err) => {});
  };
}

export const reports: angular.IComponentOptions = {
  template: require("./reports.html"),
  controller: ReportsController,
};



// WEBPACK FOOTER //
// ./src/app/reports/reports.ts